import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    private baseUrl: string;

    constructor(private httpClient: HttpClient) {
        this.baseUrl = environment.BASE_LA;
    }

    /**
     * Obtener servicios disponibles
     * GET /api/app-config/available-services
     */
    getAvailableServices = async (): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/available-services`, { headers })
            );
        } catch (error) {
            console.error('Error getting available services:', error);
            throw error;
        }
    };

    /**
     * Agregar un servicio
     * POST /api/app-config/service
     */
    addService = async (serviceData: any): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.post<any>(`${this.baseUrl}/api/app-config/service`, serviceData, { headers })
            );
        } catch (error) {
            console.error('Error adding service:', error);
            throw error;
        }
    };

    /**
     * Eliminar un servicio
     * DELETE /api/app-config/service/:code
     */
    deleteService = async (code: string): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.delete<any>(`${this.baseUrl}/api/app-config/service/${code}`, { headers })
            );
        } catch (error) {
            console.error('Error deleting service:', error);
            throw error;
        }
    };

    /**
     * Activar/Desactivar un servicio
     * PUT /api/app-config/service/toggle-status
     */
    toggleServiceStatus = async (code: string, status: boolean): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { code, status };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/service/toggle-status`, body, { headers })
            );
        } catch (error) {
            console.error('Error toggling service status:', error);
            throw error;
        }
    };

    /**
     * Consultar el valor actual de SIMON
     * GET /api/app-config/get_show_simon
     */
    getShowSimon = async (): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/get_show_simon`, { headers })
            );
        } catch (error) {
            console.error('Error getting show simon:', error);
            throw error;
        }
    };

    /**
     * Actualizar el valor de SIMON
     * POST /api/app-config/update_show_simon
     */
    updateShowSimon = async (value: boolean): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { show_simon: value };
        try {
            return lastValueFrom(
                this.httpClient.post<any>(`${this.baseUrl}/api/app-config/update_show_simon`, body, { headers })
            );
        } catch (error) {
            console.error('Error updating show simon:', error);
            throw error;
        }
    };

    // ===== Preferencias del sistema =====
    // Cache simple en memoria para que la ruta de pagos decida la vista sin re-fetch en cada navegación.
    private _preferencesCache: any = null;

    /**
     * Obtener preferencias del sistema
     * GET /api/app-config/preferences  ->  { data: { default_payments_view } }
     */
    getPreferences = async (force = false): Promise<any> => {
        if (this._preferencesCache && !force) return this._preferencesCache;
        const headers = { 'content-type': 'application/json' };
        try {
            const resp = await lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/preferences`, { headers })
            );
            this._preferencesCache = resp?.data || {};
            return this._preferencesCache;
        } catch (error) {
            console.error('Error getting preferences:', error);
            return { default_payments_view: 'classic', default_finance_payments_view: 'classic' };
        }
    };

    /**
     * Actualizar la vista de pagos por defecto
     * PUT /api/app-config/preferences/default-payments-view  body { view: 'tabla' | 'classic' }
     */
    updateDefaultPaymentsView = async (view: 'tabla' | 'classic'): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { view };
        try {
            const resp = await lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/preferences/default-payments-view`, body, { headers })
            );
            // invalidar/actualizar cache
            this._preferencesCache = { ...(this._preferencesCache || {}), default_payments_view: view };
            return resp;
        } catch (error) {
            console.error('Error updating default payments view:', error);
            throw error;
        }
    };

    /**
     * Actualizar la vista de pagos de Finanzas por defecto
     * PUT /api/app-config/preferences/default-finance-payments-view  body { view: 'tabla' | 'classic' }
     */
    updateDefaultFinancePaymentsView = async (view: 'tabla' | 'classic'): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { view };
        try {
            const resp = await lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/preferences/default-finance-payments-view`, body, { headers })
            );
            // invalidar/actualizar cache
            this._preferencesCache = { ...(this._preferencesCache || {}), default_finance_payments_view: view };
            return resp;
        } catch (error) {
            console.error('Error updating default finance payments view:', error);
            throw error;
        }
    };

    /**
     * Obtener configuración de registro
     * GET /api/app-config/register/config
     */
    getRegisterConfig = async (): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/register/config`, { headers })
            );
        } catch (error) {
            console.error('Error getting register config:', error);
            throw error;
        }
    };

    /**
     * Actualizar estado de registro
     * PUT /api/app-config/register/toggle
     */
    toggleRegisterStatus = async (enable: boolean): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { enable };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/register/toggle`, body, { headers })
            );
        } catch (error) {
            console.error('Error toggling register status:', error);
            throw error;
        }
    };

    /**
     * Actualizar mensaje de registro
     * PUT /api/app-config/register/message
     */
    updateRegisterMessage = async (text: string): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { text };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/register/message`, body, { headers })
            );
        } catch (error) {
            console.error('Error updating register message:', error);
            throw error;
        }
    };

    /**
     * Obtener configuración del modal home
     * GET /api/app-config/get_home_modal_config
     */
    getHomeModalConfig = async (): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/get_home_modal_config`, { headers })
            );
        } catch (error) {
            console.error('Error getting home modal config:', error);
            throw error;
        }
    };

    /**
     * Alternar estado del modal home
     * PUT /api/app-config/home-modal/toggle-status
     */
    toggleHomeModalStatus = async (enabled: boolean): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { enabled };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/home-modal/toggle-status`, body, { headers })
            );
        } catch (error) {
            console.error('Error toggling home modal status:', error);
            throw error;
        }
    };

    /**
     * Publish the home modal image and tap action
     * PUT /api/app-config/home-modal/content  (multipart/form-data)
     * Fields: `image` (only when the operator picked a new file), `action_type`
     * ('' | 'internal' | 'external') and `action_target` ('' when there is no action).
     * No manual content-type: the browser sets the multipart boundary.
     */
    updateHomeModalContent = async (formData: FormData): Promise<any> => {
        return lastValueFrom(
            this.httpClient.put<any>(`${this.baseUrl}/api/app-config/home-modal/content`, formData)
        );
    };

    // =====================================================
    // Fase 2 Credolab — política de aprobación + config Credolab
    // =====================================================

    /**
     * GET /api/app-config/approval-policy
     * Devuelve { mode, rotationState, updatedBy, updatedAt, createdAt, availableModes }
     * Si el doc no está sembrado, el backend responde 404 — capturar en el componente.
     */
    getApprovalPolicy = async (): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/approval-policy`, { headers })
            );
        } catch (error) {
            console.error('Error getting approval policy:', error);
            throw error;
        }
    };

    /**
     * PUT /api/app-config/approval-policy
     * Body: { mode }. El backend valida enum y no-op (unchanged:true).
     */
    updateApprovalPolicyMode = async (mode: string): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { mode };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/approval-policy`, body, { headers })
            );
        } catch (error) {
            console.error('Error updating approval policy mode:', error);
            throw error;
        }
    };

    /**
     * GET /api/app-config/credolab-config
     * Devuelve { minScore, maxProbability, requireBoth, autoRefuseFlags, availableFlags, updatedBy, updatedAt }
     */
    getCredolabConfig = async (): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.get<any>(`${this.baseUrl}/api/app-config/credolab-config`, { headers })
            );
        } catch (error) {
            console.error('Error getting credolab config:', error);
            throw error;
        }
    };

    /**
     * PUT /api/app-config/credolab-config/thresholds
     * Body: { minScore?, maxProbability?, requireBoth?, evaluationMode? } — al menos uno requerido.
     */
    updateCredolabThresholds = async (payload: {
        minScore?: number;
        maxProbability?: number;
        requireBoth?: boolean;
        evaluationMode?: 'BOTH' | 'EITHER' | 'SCORE_ONLY' | 'PROBABILITY_ONLY';
        // Interruptor de modelo: null = automático (decide el modelo anterior
        // mientras Credolab lo siga mandando).
        scoreModel?: 'score' | 'score_v2' | null;
        // Umbrales propios por modelo. null en un campo = heredar el global.
        models?: Record<string, { minScore?: number | null; maxProbability?: number | null }>;
    }): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/credolab-config/thresholds`, payload, { headers })
            );
        } catch (error) {
            console.error('Error updating credolab thresholds:', error);
            throw error;
        }
    };

    /**
     * PUT /api/app-config/credolab-config/flags
     * Body: { flagName, enabled }. El backend valida whitelist.
     */
    updateCredolabFlag = async (flagName: string, enabled: boolean): Promise<any> => {
        const headers = { 'content-type': 'application/json' };
        const body = { flagName, enabled };
        try {
            return lastValueFrom(
                this.httpClient.put<any>(`${this.baseUrl}/api/app-config/credolab-config/flags`, body, { headers })
            );
        } catch (error) {
            console.error('Error updating credolab flag:', error);
            throw error;
        }
    };
}
